import { decodeJwtPayload, getUserTypeFromToken, type HubUserType } from '@apexx/auth';
import { preferences } from '@apexx/storage';

import type { IdentifierMode } from '@/auth/otpIdentifierMode';

export const LAST_IDENTITY_PREFS_KEY = 'apexx.hub.lastIdentity';

/** Snapshot of the last signed-in hub user kept across soft session teardown. */
export type HubLastIdentity = {
	sub: string;
	displayName: string;
	email?: string;
	phone?: string;
	/** Identifier used for the last OTP login (phone or email string). */
	otpIdentifier?: string;
	identifierMode?: IdentifierMode;
	userType: HubUserType;
	updatedAt: number;
};

type JwtIdentityPayload = {
	sub?: string;
	name?: string;
	preferred_username?: string;
	email?: string;
	phone_number?: string;
};

type OtpIdentityExtras = {
	otpIdentifier?: string;
	identifierMode?: IdentifierMode;
};

function firstNonEmpty(...values: Array<string | undefined>): string | undefined {
	for (const value of values) {
		const trimmed = value?.trim();
		if (trimmed) {
			return trimmed;
		}
	}

	return undefined;
}

function inferIdentifierMode(identifier: string, explicit?: IdentifierMode): IdentifierMode {
	if (explicit) {
		return explicit;
	}

	return identifier.includes('@') ? 'email' : 'phone';
}

function isHubLastIdentity(value: unknown): value is HubLastIdentity {
	if (!value || typeof value !== 'object') {
		return false;
	}

	const record = value as Record<string, unknown>;
	return (
		typeof record.sub === 'string'
		&& record.sub.length > 0
		&& typeof record.displayName === 'string'
		&& record.displayName.length > 0
		&& (record.userType === 'client' || record.userType === 'staff')
		&& typeof record.updatedAt === 'number'
	);
}

/** Reads the persisted last-identity snapshot, if any. */
export async function getLastIdentity(): Promise<HubLastIdentity | null> {
	try {
		const raw = await preferences.getItem(LAST_IDENTITY_PREFS_KEY);
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw) as unknown;
		return isHubLastIdentity(parsed) ? parsed : null;
	} catch {
		return null;
	}
}

/** Persists a last-identity snapshot (survives soft unauthorized / session expiry). */
export async function setLastIdentity(identity: HubLastIdentity): Promise<void> {
	try {
		await preferences.setItem(LAST_IDENTITY_PREFS_KEY, JSON.stringify(identity));
	} catch {
		// Ignore quota / private-mode failures; re-auth simply falls back to full login.
	}
}

/** Clears the last-identity snapshot (intentional full logout only). */
export async function clearLastIdentity(): Promise<void> {
	try {
		await preferences.removeItem(LAST_IDENTITY_PREFS_KEY);
	} catch {
		// Ignore storage failures.
	}
}

/**
 * Builds / merges a last-identity snapshot from an access token.
 * Preserves a prior OTP identifier when the new token does not replace it.
 */
export async function rememberLastIdentityFromToken(
	token: string,
	extras: OtpIdentityExtras = {},
): Promise<HubLastIdentity | null> {
	const payload = decodeJwtPayload(token) as JwtIdentityPayload | undefined;
	const sub = payload?.sub?.trim();
	if (!sub) {
		return null;
	}

	const previous = await getLastIdentity();
	const email = firstNonEmpty(payload?.email, previous?.sub === sub ? previous.email : undefined);
	const phone = firstNonEmpty(
		payload?.phone_number,
		previous?.sub === sub ? previous.phone : undefined,
	);
	const displayName = firstNonEmpty(
		payload?.name,
		payload?.preferred_username,
		email,
		phone,
		previous?.sub === sub ? previous.displayName : undefined,
		'Signed out',
	);

	const otpIdentifier = firstNonEmpty(
		extras.otpIdentifier,
		previous?.sub === sub ? previous.otpIdentifier : undefined,
		email,
		phone,
	);
	const identifierMode = otpIdentifier
		? inferIdentifierMode(
			otpIdentifier,
			extras.identifierMode ?? (previous?.sub === sub ? previous.identifierMode : undefined),
		)
		: undefined;

	const identity: HubLastIdentity = {
		sub,
		displayName: displayName ?? 'Signed out',
		userType: getUserTypeFromToken(token),
		updatedAt: Date.now(),
		...(email ? { email } : {}),
		...(phone ? { phone } : {}),
		...(otpIdentifier ? { otpIdentifier, identifierMode } : {}),
	};

	await setLastIdentity(identity);
	return identity;
}

/** OTP continue target derived from a stored identity, if enough data exists. */
export function resolveOtpContinueFromIdentity(
	identity: HubLastIdentity,
): { identifier: string; identifierMode: IdentifierMode } | null {
	const identifier = firstNonEmpty(identity.otpIdentifier, identity.email, identity.phone);
	if (!identifier) {
		return null;
	}

	return {
		identifier,
		identifierMode: inferIdentifierMode(identifier, identity.identifierMode),
	};
}
